import { SMS_PRICE_HUF, formatMoney } from "@szerviz/core";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { renderSVG } from "uqr";
import { useApp } from "../context";
import type { SmsUsage } from "../data";
import { ErrorText } from "./ErrorText";

/** The link the phone opens (QR code): the phone setup page with the one-time code. */
export const phoneSetupLink = (token: string) => `${globalThis.location?.origin ?? ""}${globalThis.location?.pathname ?? "/"}#phone-setup/${token}`;

/**
 * SMS sending ("sms" feature): who sends (Garagevo for a fee or the own phone),
 * this month's counter, setting up the own Android phone with a QR code, test SMS.
 */
export function SmsSettings({ isOwner, canEdit }: { isOwner: boolean; canEdit: boolean }) {
  const { t, i18n } = useTranslation();
  const { store, workshop, reloadWorkshop } = useApp();
  const [usage, setUsage] = useState<SmsUsage | null>(null);
  const [phoneSet, setPhoneSet] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [to, setTo] = useState("");
  const [info, setInfo] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const on = workshop.features.includes("sms");
  const ownPhone = workshop.general.smsProvider === "ownPhone";
  const money = (minor: number) => formatMoney(minor, "HUF", i18n.language);

  const load = useCallback(async () => {
    if (!on) return;
    setUsage(await store.smsUsage(workshop.id));
    setPhoneSet(await store.smsPhoneSet(workshop.id));
  }, [on, store, workshop.id]);
  useEffect(() => void load(), [load]);
  if (!on) return null;

  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    setInfo(null);
    try {
      await fn();
    } catch (err) {
      setError(err);
    }
    await load();
  };
  const test = () =>
    void run(async () => {
      const r = await store.sendTestSms(workshop.id, to, t("sms.testText", { workshop: workshop.name }));
      setInfo(t("sms.testSent", { to: r.to }));
    });

  return (
    <section className="sms-settings" aria-label={t("sms.title")}>
      <h2>{t("sms.title")}</h2>
      <fieldset className="choice-cards" disabled={!canEdit}>
        <legend>{t("setting.smsProvider.label")}</legend>
        {(["garagevo", "ownPhone"] as const).map((v) => (
          <label key={v} className={`choice-card${(ownPhone ? "ownPhone" : "garagevo") === v ? " selected" : ""}`}>
            <input
              type="radio"
              name="smsProvider"
              value={v}
              checked={(ownPhone ? "ownPhone" : "garagevo") === v}
              onChange={() =>
                void run(async () => {
                  await store.saveGeneralSettings(workshop.id, { ...workshop.general, smsProvider: v });
                  await reloadWorkshop();
                })
              }
            />
            <span>
              <strong>{t(`setting.smsProvider.option.${v}`)}</strong>
              <br />
              <span className="muted">{v === "ownPhone" ? t("sms.providerOwnPhone") : t("sms.providerGaragevo", { price: money(SMS_PRICE_HUF) })}</span>
            </span>
          </label>
        ))}
      </fieldset>
      <p className="muted">{t("sms.providerChange")}</p>
      {usage && <p><strong>{t("sms.usage", { count: usage.count, amount: money(usage.amount) })}</strong></p>}
      <ErrorText error={error} />
      {info && <p className="ok">{info}</p>}

      {ownPhone && (
        <div className="sms-phone">
          <p className={phoneSet ? "ok" : "warn"}>{phoneSet ? t("sms.phoneReady") : t("sms.phoneNotReady")}</p>
          {isOwner && !token && (
            <div className="actions">
              <button type="button" className={phoneSet ? "" : "primary"} onClick={() => void run(async () => setToken(await store.createSmsSetupToken(workshop.id)))}>
                {t("sms.phoneSetupStart")}
              </button>
              {phoneSet && (
                <button type="button" className="danger" onClick={() => void run(() => store.clearSmsPhone(workshop.id))}>
                  {t("sms.phoneRemove")}
                </button>
              )}
            </div>
          )}
          {token && (
            <div className="sms-qr">
              <p>{t("sms.phoneSetupScan")}</p>
              <div className="qr" aria-label={t("sms.qr")} dangerouslySetInnerHTML={{ __html: renderSVG(phoneSetupLink(token)) }} />
              <input className="quote-link" readOnly value={phoneSetupLink(token)} aria-label={t("sms.qrLink")} onFocus={(e) => e.target.select()} />
              <div className="actions">
                <button type="button" onClick={() => { setToken(null); void load(); }}>{t("sms.phoneSetupDone")}</button>
              </div>
            </div>
          )}
        </div>
      )}

      {canEdit && (!ownPhone || phoneSet) && (
        <div className="actions sms-test">
          <label className="field">
            <span>{t("sms.testTo")}</span>
            <input type="tel" value={to} onChange={(e) => setTo(e.target.value)} aria-label={t("sms.testTo")} />
          </label>
          <button type="button" onClick={test} disabled={!to.trim()}>
            {t("sms.test")}
          </button>
        </div>
      )}
    </section>
  );
}
